import React, { useState, useEffect, useRef } from "react";
import { Languages } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

/**
 * Slice J + K · Inline chat auto-translation.
 *
 * Behaviour:
 *   · Auto-translations OFF     → render nothing (silent)
 *   · Auto-translations ON  and message is already in user's language → nothing
 *   · Auto-translations ON  and message is in another language        → fetch
 *     translation on mount and render it inline; chip lets the user
 *     toggle the translation off / back on.
 *
 * Detection is a fast Unicode-block heuristic; the backend endpoint returns
 * the definitive translation so mis-fires just short-circuit.
 */

// Cache translations across bubbles for the current session so re-mounts
// (scrolling, pagination) never re-charge the LLM for the same text.
const _CACHE = new Map();          // key = `${target}:::${text}` → translated

function guessLang(text) {
  const s = text || "";
  if (!s) return null;
  if (/[\u0900-\u097F]/.test(s)) return "hi"; // Devanagari
  if (/[\u4E00-\u9FFF]/.test(s)) return "zh"; // CJK
  if (/[\u3040-\u30FF]/.test(s)) return "ja";
  if (/[\uAC00-\uD7AF]/.test(s)) return "ko";
  if (/[\u0600-\u06FF]/.test(s)) return "ar";
  if (/[\u0400-\u04FF]/.test(s)) return "ru";
  // Latin — try common markers.
  if (/\b(xin chào|cảm ơn|của)\b/i.test(s)) return "vi";
  if (/\b(hola|gracias|por favor|buenos)\b/i.test(s)) return "es";
  if (/\b(bonjour|merci|s'il|c'est)\b/i.test(s)) return "fr";
  if (/\b(danke|bitte|hallo|guten)\b/i.test(s)) return "de";
  return "en";
}

export default function TranslateChip({ text, className = "", targetLang }) {
  const { lang } = useI18n();
  const [translated, setTranslated] = useState(null);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);
  const [hidden, setHidden]         = useState(false);
  const fetchedRef = useRef(false);

  // Gate 1: user opted-in.
  let autoOn = true;
  try { autoOn = (localStorage.getItem("f2n_auto_translate") ?? "1") === "1"; } catch { /* ignore */ }
  const clean = (text || "").trim();
  const detected = guessLang(clean);
  // targetLang wins (used for outgoing messages → recipient's language),
  // else fall back to the current user's UI language.
  const target = (targetLang || lang || "en").slice(0, 2);
  const needs  = autoOn && clean.length >= 2 && detected && detected !== target;

  // Auto-fetch as soon as we're mounted with a message worth translating.
  useEffect(() => {
    if (!needs || fetchedRef.current) return;
    fetchedRef.current = true;
    const key = `${target}:::${clean}`;
    if (_CACHE.has(key)) {
      setTranslated(_CACHE.get(key));
      return;
    }
    setLoading(true);
    api.post("/ai/translate", { text: clean, target_lang: target, source_lang: detected })
      .then(r => {
        const out = r.data?.translated || "";
        _CACHE.set(key, out);
        setTranslated(out);
        setError(null);
      })
      .catch(() => setError("Translation unavailable"))
      .finally(() => setLoading(false));
  }, [clean, target, detected, needs]);

  if (!needs) return null;

  return (
    <div className={className} data-testid="translate-chip-wrap">
      {!hidden && translated && (
        <div
          className="mt-1 text-sm rounded-lg bg-emerald-50 border border-emerald-100 px-2 py-1.5 text-emerald-900 whitespace-pre-wrap"
          data-testid="translate-body"
        >
          {translated}
        </div>
      )}
      <button
        onClick={() => setHidden(h => !h)}
        className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 hover:text-emerald-800"
        data-testid="translate-chip"
      >
        <Languages size={11} />
        {loading
          ? "Translating…"
          : error
            ? error
            : hidden
              ? `See translation (${target.toUpperCase()})`
              : "Hide translation"}
      </button>
    </div>
  );
}
